import { Cape, Color, Pants, Sex, SexCapes, SexPants, SexShirts, SexShoes, Shirt, Shoe } from "../character/character"
import {
    activateInventoryItem,
    addInventoryItem,
    CraftableItemTypeMeta,
    createInventoryItem,
    ItemCharacteristics,
    ItemClass,
    ItemMaterial,
    ItemRecipe,
    ItemTypeMeta,
    locateInventoryActiveItemObject,
    locateInventoryItemObjectByItemTypeMeta,
    maxInventoryItemsSize,
    resolveItemTypeToMeta,
    TradingValueItemTypeMeta,
    updateInventoryItemQuantity,
} from "../item/item"
import { ItemClassTypes } from "../item/item_class"
import { ItemClassCharacteristics } from "../item/item_class_characteristics"
import { ITEM_SOURCE_HINTS } from "../item/crafting_groups"
import { itemRecipes } from "../item/item_crafting"

// Recipes held back from the crafting list. The ONE escape hatch now that everything else is shown.
//
// wall-stone costs 3 planks -- exactly what a wooden wall costs, with no stone in it at all. Hidden,
// that was a data quirk; shown, it is two walls at the same price where one sounds strictly better.
// It comes back when it costs something of its own.
//
// Note this is a real hide, not the old half-hide: under the previous rule wall-stone was skipped by
// the always-list pass and then walked straight back in through "holds some of every ingredient", so
// it appeared for anyone carrying planks -- which is everyone who has built anything.
const UNLISTED_RECIPES: string[] = ["wall-stone"]
import { BaseTradeValue } from "../item/item_trade"
import { AllItemTypes, Ammo, ItemTypes } from "../item/item_type"
import { ItemTypeCharacteristics } from "../item/item_type_characteristics"
import { ItemTypesProtection } from "../item/item_type_protection"
import { ItemTypesWeight } from "../item/item_type_weight"
import { AmmoMeta, Entity, Inventory, InventoryItem, TileSize, WeaponCharacteristics, WeaponType } from "../models"
import { FeatureCatalogs } from "../room/feature_catalog"
import { FragmentPointer } from "../room/house/feature/house"
import { Skill } from "../skills/skills"
import { randomId } from "../util"
import { Mechanics } from "./mechanics"

class Weight {
    static maximumItemWeight = 15
    static maximumTotalItemWeight = 0

    static {
        this.maximumTotalItemWeight = ItemClassTypes.map(type => {
            const itemTypeWeights = ItemTypesWeight[type] || {}
            const sorted = Object.keys(itemTypeWeights)
                .map(t => itemTypeWeights[t])
                .sort()
            return sorted[sorted.length - 1] || 0
        }).reduce((a, n) => a + n * this.maximumItemWeight, 0)
    }

    static computeItemWeight = (item: InventoryItem) => {
        const { itemTypeMeta } = item
        if (!itemTypeMeta) {
            return 0
        }

        const { type } = itemTypeMeta
        const itemTypes = ItemTypesWeight[itemTypeMeta.class]
        if (!itemTypes) {
            return 0
        }

        return (itemTypes[type] || 0) * this.maximumItemWeight
    }

    static computeWeight = (inventory: Inventory) => {
        // only consider active (equipped) items
        const activeItems = inventory.items?.filter(item => item.active)
        return activeItems?.map(i => this.computeItemWeight(i)).reduce((a, n) => a + n, 0)
    }

    static computeWeightPctOfMax = (inventory: Inventory) => {
        const f = this.computeWeight(inventory)
        return f / this.maximumTotalItemWeight
    }
}

class Protection {
    static maximumItemProtection = 15
    static maximumTotalItemProtection = 0

    static {
        this.maximumTotalItemProtection = ItemClassTypes.map(type => {
            const itemTypeProtections = ItemTypesProtection[type] || {}
            const sorted = Object.keys(itemTypeProtections)
                .map(t => itemTypeProtections[t])
                .sort()

            return sorted[sorted.length - 1] || 0
        }).reduce((a, n) => a + n * this.maximumItemProtection, 0)
    }

    static computeItemProtection = (item: InventoryItem) => {
        return this.computeItemProtectionPct(item.itemTypeMeta) * this.maximumItemProtection
    }

    static computeItemProtectionPct = (itemTypeMeta: ItemTypeMeta) => {
        if (!itemTypeMeta) {
            return 0
        }

        const { type } = itemTypeMeta
        const itemTypes = ItemTypesProtection[itemTypeMeta.class]
        if (!itemTypes) {
            return 0
        }

        return itemTypes[type] || 0
    }

    static computeProtection = (inventory: Inventory) => {
        // only consider active (equipped) items
        const activeItems = inventory.items?.filter(item => item.active) || []
        return activeItems.map(i => this.computeItemProtection(i)).reduce((a, n) => a + n, 0)
    }

    static computeProtectionPctOfMax = (inventory: Inventory) => {
        const f = this.computeProtection(inventory)
        return f / this.maximumTotalItemProtection
    }
}

class Properties {
    private static hasProperty = (itemTypeMeta: ItemTypeMeta,
        prop: Exclude<keyof ItemCharacteristics, "placementRange" | "placementAction">): boolean => {
        if (prop === "disallowedClassesOnActive" || prop === "buildType") {
            return (
                !!ItemTypeCharacteristics[itemTypeMeta.type]?.[prop] ||
                !!ItemClassCharacteristics[itemTypeMeta.class]?.[prop]
            )
        }

        if (ItemTypeCharacteristics[itemTypeMeta.type]?.[prop] !== undefined) {
            return ItemTypeCharacteristics[itemTypeMeta.type][prop]
        }
        const klass = ItemClassCharacteristics[itemTypeMeta.class]
        return klass?.[prop]
    }

    static toItemTypeMeta = (itemType: ItemTypes, color?: Color) => {
        return resolveItemTypeToMeta(itemType, color, true)
    }

    static toSpriteId = (itemType: ItemTypes, color?: Color) => {
        const itemTypeMeta = this.toItemTypeMeta(itemType, color)
        if (!itemTypeMeta) {
            return ""
        }
        return itemTypeMeta.spriteId || `${itemTypeMeta.type}-${itemTypeMeta.color}.png`
    }

    static buildable = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "buildable")
    }

    static exclusiveReadiness = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "exclusiveReadiness")
    }

    static buildType = (itemTypeMeta: ItemTypeMeta): BuildType => {
        return ItemTypeCharacteristics[itemTypeMeta.type].buildType
    }

    static disallowedClassesOnActive = (itemTypeMeta: ItemTypeMeta): ItemClass[] => {
        if (!this.hasProperty(itemTypeMeta, "disallowedClassesOnActive")) {
            return []
        }

        return (
            ItemClassCharacteristics[itemTypeMeta.class]["disallowedClassesOnActive"] ||
            ItemTypeCharacteristics[itemTypeMeta.type]["disallowedClassesOnActive"] ||
            []
        )
    }

    static useable = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "useable")
    }

    /** Items selected for use with the primary world action. */
    static primaryUseable = (itemTypeMeta: ItemTypeMeta) =>
        !!itemTypeMeta && (this.hasProperty(itemTypeMeta, "primaryUseable") || this.placement(itemTypeMeta)?.action === "use")

    static placement = (itemTypeMeta: ItemTypeMeta) => {
        const type = ItemTypeCharacteristics[itemTypeMeta?.type]
        const klass = ItemClassCharacteristics[itemTypeMeta?.class]
        const range = type?.placementRange ?? klass?.placementRange
        const action = type?.placementAction ?? klass?.placementAction
        return range !== undefined && action ? { range, action } : undefined
    }

    static readyable = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "readyable")
    }

    static showWorldOnUse = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "useHasWorldEffect")
    }

    static stackable = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "stackable")
    }

    static unsellable = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "unsellable")
    }

    static unbuyable = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "unbuyable")
    }

    static hasDurability = (itemTypeMeta: ItemTypeMeta) => {
        return this.hasProperty(itemTypeMeta, "hasDurability")
    }

    static currency = (itemTypeMeta: ItemTypeMeta) => {
        // todo make this less brittle
        if (itemTypeMeta.type === "shard-metal") {
            return true
        }

        return false
    }

    static equippable = (itemTypeMeta: ItemTypeMeta, entity: Entity) => {
        if (!ItemClassCharacteristics[itemTypeMeta.class]?.equippable) {
            return false
        }

        if (itemTypeMeta.class === "shirt") {
            const items = SexShirts[entity.appearance?.sex]
            return !!items && !!items.find(i => i === itemTypeMeta.type)
        }

        if (itemTypeMeta.class === "pants") {
            const items = SexPants[entity.appearance?.sex]
            return !!items && !!items.find(i => i === itemTypeMeta.type)
        }

        if (itemTypeMeta.class === "shoes") {
            const items = SexShoes[entity.appearance?.sex]
            return !!items && !!items.find(i => i === itemTypeMeta.type)
        }

        if (itemTypeMeta.class === "cape") {
            const items = SexCapes[entity.appearance?.sex]
            return !!items && !!items.find(i => i === itemTypeMeta.type)
        }

        return true
    }

    static itemSex = (itemTypeMeta: ItemTypeMeta): Sex | "both" | "na" => {
        const { type, class: itemClass } = itemTypeMeta
        const evaluator = (male: boolean, female: boolean) => {
            if (male && female) {
                return "both"
            }
            if (!male && female) {
                return "female"
            }
            if (male && !female) {
                return "male"
            }
            return "na"
        }

        if (itemClass === "cape") {
            return evaluator(SexCapes["male"]?.includes(type as Cape), SexCapes["female"]?.includes(type as Cape))
        }

        if (itemClass === "pants") {
            return evaluator(SexPants["male"]?.includes(type as Pants), SexPants["female"]?.includes(type as Pants))
        }

        if (itemClass === "shirt") {
            return evaluator(SexShirts["male"]?.includes(type as Shirt), SexShirts["female"]?.includes(type as Shirt))
        }

        if (itemClass === "shoes") {
            return evaluator(SexShoes["male"]?.includes(type as Shoe), SexShoes["female"]?.includes(type as Shoe))
        }

        return "na"
    }
}

class Trading {
    static isCurrency = (item: InventoryItem) => {
        return ItemTypeCharacteristics[item.itemTypeMeta.type]?.isCurrency
    }

    static isAmmo = (item: InventoryItem) => {
        return ItemTypeCharacteristics[item.itemTypeMeta.type]?.isAmmo
    }

    static isItemSellable = (item: InventoryItem) => {
        if (Properties.unsellable(item.itemTypeMeta)) {
            return false
        }
        const baseTradeValue = BaseTradeValue[item.itemTypeMeta.type]
        if (baseTradeValue && BaseTradeValue[item.itemTypeMeta.type]?.quantity < 1) {
            return false
        }

        return true
    }

    static computeSellValueCost = (itemTypeMeta: ItemTypeMeta, quantity: number): TradingValueItemTypeMeta => {
        const base = BaseTradeValue[itemTypeMeta.type]
        if (base) {
            // markdown; todo, this might depend on the seller and the buyer
            const markup = 1
            return {
                itemType: base.itemType,
                quantity: Math.ceil(base.quantity * markup * quantity),
            }
        }

        if (itemTypeMeta.class === "resource") {
            return {
                itemType: "shard-metal",
                quantity: 1,
            }
        }

        return {
            itemType: "shard-metal",
            quantity: 10,
        }
    }

    static isItemBuyable = (itemTypeMeta: ItemTypeMeta): boolean => {
        return true
    }

    static computeBuyValueCost = (itemType: ItemTypes, quantity: number): TradingValueItemTypeMeta => {
        const base = BaseTradeValue[itemType]
        if (!base) {
            return {
                itemType: "shard-metal",
                quantity: 10,
            }
        }
        // markup; todo, this might depend on the seller and the buyer
        const markup = 1.25
        return {
            itemType: base.itemType,
            quantity: Math.ceil(base.quantity * markup * quantity),
        }
    }

    static getTradableItemTypes = () => {
        const buyable = []
        Object.keys(AllItemTypes).forEach(itemType => {
            const itemTypeMeta = ItemMechanics.properties.toItemTypeMeta(itemType as ItemTypes)
            if (!ItemMechanics.properties.unbuyable(itemTypeMeta)) {
                buyable.push(itemTypeMeta)
            }
        })

        return buyable
    }
}

export const buildTypes = ["wall", "decor", "entity", "roof", "floor", "floor2"] as const
export type BuildType = (typeof buildTypes)[number]
export type CraftableEntityType = "door" | "chest"

export interface CraftableMeta {
    buildType: BuildType
    fragmentPointer?: FragmentPointer
    entityType?: CraftableEntityType
    itemType?: ItemTypes
}

interface Ingredient {
    itemType: ItemTypes
    quantity: number
}

/**
 * One line of a recipe's price, with what the player actually holds against it.
 *
 * `required` and `held` come out of the SAME pass that decides whether the recipe is craftable, which
 * is the point: the crafting panel used to state a recipe's cost from one count ("3 stick (have 10)",
 * summed by type) and gate the Craft button on another (the last stack of that type), so a row could
 * swear you could afford it and refuse to make it. One number now, read by both.
 */
export interface IngredientCost {
    itemType: ItemTypes
    required: number
    held: number
    // Does this material have a recipe of its own? A shortfall the player can go and MAKE is a
    // different kind of problem from one they have to go and find, and the panel offers each the
    // right next step: a jump to that recipe, or a line saying where the stuff comes from.
    craftable?: boolean
    // ...which is that line, for a material with no recipe. "Cut long grass", for straw.
    source?: string
}

export interface CraftableReport {
    craftable: boolean
    isValid: boolean
    missingIngredients?: Ingredient[]
    missingSkills?: Skill[]
    // Every ingredient, in recipe order, affordable or not. Absent when there is no recipe at all.
    cost?: IngredientCost[]
    // How many the recipe makes in one go. A stick recipe yields FIVE, which nothing used to say.
    yield?: number
}

class Crafting {
    private static toKey = (next: ItemTypeMeta): string => next.class + "-" + next.type + "-" + next.color

    /**
     * How much of each resource the player is holding, keyed the way a recipe's ingredients are.
     *
     * SUMMED across stacks, which is the whole point of pulling this out. Both callers used to build
     * this with `{...acc, [key]: quantity}` -- an overwrite, not an add -- so only the LAST stack of a
     * resource counted. 8 sticks and then 2 read as 2, which greyed out a recipe needing 5 while the
     * cost line beside it (which does sum, by type) said "have 10". The verdict even depended on the
     * order the stacks sat in the pack: 2-then-8 crafted, 8-then-2 did not.
     *
     * Stacks of one resource normally merge, so this bit past the 50-item stack limit or wherever a
     * colour split one type into two keys -- but the symptom was a recipe the panel swore you could
     * afford and would not make.
     */
    private static heldResources = (inventory: Inventory): Record<string, number> =>
        (inventory?.items || [])
            .filter(item => item.itemTypeMeta?.class === "resource")
            .reduce((acc, next) => {
                const key = this.toKey(next.itemTypeMeta)
                acc[key] = (acc[key] || 0) + (next.quantity || 1)
                return acc
            }, {} as Record<string, number>)

    static getRecipeFor = (toFind: ItemTypeMeta): ItemRecipe => {
        return itemRecipes.find(next => this.toKey(next.itemTypeMeta) === this.toKey(toFind))
    }

    static isCraftable = (itemTypeMeta: ItemTypeMeta, inventory: Inventory, skills: Skill[] = []): CraftableReport => {
        const recipe = itemRecipes.find(
            r => r.itemTypeMeta.type === itemTypeMeta.type && itemTypeMeta.color == r.itemTypeMeta.color,
        )
        if (!recipe) {
            return {
                craftable: false,
                isValid: false,
            }
        }

        const resourceMap = this.heldResources(inventory)

        const { requiredSkills = [] } = recipe
        if (requiredSkills.length > 0 && !!requiredSkills.find(s => !skills.includes(s))) {
            return {
                craftable: false,
                isValid: false,
            }
        }

        const missingIngredients: Ingredient[] = []
        const cost: IngredientCost[] = []
        const foundIngredients = recipe.ingredients.filter(required => {
            const toLookupKey = this.toKey(required.itemTypeMeta)
            const ownedQuantity = resourceMap[toLookupKey] || 0
            const found = ownedQuantity >= required.quantity
            const ingredientType = required.itemTypeMeta.type
            const hasRecipe = !!this.getRecipeFor(required.itemTypeMeta)
            cost.push({
                itemType: ingredientType,
                required: required.quantity,
                held: ownedQuantity,
                craftable: hasRecipe,
                // Only for a LEAF. A material with a recipe already has its next step, and two
                // answers to "how do I get this" is worse than one.
                source: hasRecipe ? undefined : ITEM_SOURCE_HINTS[ingredientType as ItemTypes],
            })
            if (!found) {
                // From the RECIPE, not from resourceMap. resourceMap only holds what the player is
                // carrying, so an ingredient they have NONE of -- the common case for anything worth
                // showing them -- resolved to undefined and printed as "undefined x3". It was masked
                // while the list only offered recipes you already held every ingredient for.
                missingIngredients.push({
                    itemType: required.itemTypeMeta.type,
                    quantity: Math.abs(ownedQuantity - required.quantity),
                })
            }
            return found
        })
        const craftable = foundIngredients.length === recipe.ingredients.length
        return {
            craftable,
            isValid: true,
            missingIngredients,
            cost,
            yield: recipe.quantity,
        }
    }

    /**
     * Which of the recipes a player is PERMITTED to see are actually offered.
     *
     * All of them, minus UNLISTED_RECIPES.
     *
     * It used to answer one question with another: a recipe appeared only once you already held every
     * ingredient for it (structures excepted), so the tree hid behind itself. An arrow is 2 sticks, 3
     * feathers and 2 metal shards, which meant the recipe did not EXIST until the moment a player
     * happened to be carrying all three at once -- and a player with no reason to keep feathers never
     * had that moment. From a playtest: "I didn't see a way to craft arrows." The same rule hid the
     * stick dagger, which costs five sticks and nothing else, from anyone not holding a stick.
     *
     * The crafting screen is where you go to find out what to COLLECT. Gating it on what you are
     * already carrying is backwards, and it is not a volume problem either: this is twenty-odd
     * recipes across four groups, a page rather than a catalogue.
     *
     * A ROOM can still narrow it, and that is the second lever: `absentMaterials` names the raw
     * materials this place does not have, and any recipe whose tree bottoms out in one of them drops
     * off the list. An ember camp is a forest with no ore in it, so the whole metal branch -- the
     * bars, and the axe and swords made from them -- is not a goal a camper can form. Expressed as
     * MATERIALS rather than as a list of recipes so that it keeps up on its own: a pickaxe added
     * tomorrow is absent from a camp without anybody remembering to say so.
     *
     * Staging is a third question with a third answer. `requiredSkills` is applied before both and is
     * the lever for deliberately holding content back -- a thing that is TRUE of a player
     * ("you have not learned smithing") rather than an accident of their pockets. Nothing uses it in
     * the data yet; the metal tree is the obvious first customer, and it sorts to the bottom of
     * Materials in the meantime because the panel offers what is craftable first.
     */
    private static computeListedRecipes = (permitted: ItemRecipe[], absentMaterials: string[] = []): ItemRecipe[] => {
        const absent = new Set(absentMaterials.map(material => material.trim()).filter(Boolean))
        return permitted.filter(recipe => {
            if (UNLISTED_RECIPES.includes(recipe.itemTypeMeta.type as string)) {
                return false
            }
            if (absent.size < 1) {
                return true
            }
            const raw = this.rawMaterialsFor(recipe)
            return ![...raw].some(material => absent.has(material))
        })
    }

    // Memoised: the recipe table never changes at runtime, and this is walked for every recipe on
    // every render of the crafting panel.
    private static rawMaterialCache: Map<string, Set<string>> = new Map()

    /**
     * Every material a recipe bottoms out in -- the leaves of its tree, not its direct ingredients.
     *
     * This is what lets a room say "there is no ore here" and have the whole metal branch follow. An
     * axe's ingredients are an iron bar and a stick, neither of which is ore; it is only two steps
     * down that the iron appears. Listing the axe as unavailable by hand would work until somebody
     * added a recipe for a pickaxe.
     *
     * Cycle-safe by construction: `seen` guards a material that is (directly or otherwise) made of
     * itself, which the data does not do today but which would otherwise hang the server rather than
     * draw a wrong list.
     */
    private static rawMaterialsFor = (recipe: ItemRecipe): Set<string> => {
        const key = this.toKey(recipe.itemTypeMeta)
        const cached = this.rawMaterialCache.get(key)
        if (cached) {
            return cached
        }

        const raw = new Set<string>()
        const seen = new Set<string>()
        const walk = (next: ItemRecipe) => {
            const nextKey = this.toKey(next.itemTypeMeta)
            if (seen.has(nextKey)) {
                return
            }
            seen.add(nextKey)
            next.ingredients.forEach(ingredient => {
                const made = this.getRecipeFor(ingredient.itemTypeMeta)
                if (made) {
                    walk(made)
                    return
                }
                // No recipe: this is something the player goes and finds, which is exactly what a
                // room can be without.
                raw.add(ingredient.itemTypeMeta.type as string)
            })
        }
        walk(recipe)

        this.rawMaterialCache.set(key, raw)
        return raw
    }

    static computeCraftableItemTypes = (
        inventory: Inventory,
        skills: Skill[] = [],
        options: { absentMaterials?: string[] } = {},
    ): CraftableItemTypeMeta[] => {
        const resourceMap = this.heldResources(inventory)

        // Permission is a separate question from affordability, and `requiredSkills` answers it. It
        // is applied FIRST and on its own, so a recipe the player may not see cannot drag its
        // ingredients onto the list either -- which is how the deep tree gets staged later.
        const permitted = itemRecipes.filter(recipe => {
            const { requiredSkills = [] } = recipe
            return requiredSkills.length < 1 || !requiredSkills.find(s => !skills.includes(s))
        })

        return this.computeListedRecipes(permitted, options.absentMaterials).map(recipe => {
            const foundIngredients = recipe.ingredients.filter(required => {
                const toLookupKey = this.toKey(required.itemTypeMeta)
                const ownedQuantity = resourceMap[toLookupKey] || 0
                return ownedQuantity >= required.quantity
            })
            return {
                itemTypeMeta: recipe.itemTypeMeta,
                craftable: foundIngredients.length === recipe.ingredients.length,
            }
        })
    }

    static computeCraftableMeta = (item: InventoryItem): CraftableMeta => {
        return this.computeCraftableMetaFromItemType(item?.itemTypeMeta?.type)
    }

    static computeCraftableMetaFromItemType = (itemType: ItemTypes): CraftableMeta => {
        if (!itemType) {
            return null
        }

        const pointer = FeatureCatalogs.instance().fragmentByItemType[itemType]
        if (pointer) {
            const feature = FeatureCatalogs.instance().featureByTag(pointer.catalog, pointer.tag)
            return feature?.craftableMeta
        }

        const itemTypeMeta = resolveItemTypeToMeta(itemType)
        const buildType = Mechanics.item.properties.buildType(itemTypeMeta)
        if (buildType) {
            return {
                itemType,
                buildType,
            }
        }

        return null
    }
}

class Projectile {
    private static ammoTypeToMeta: Map<Ammo, AmmoMeta>

    static resolveAmmoMetaFromAmmo = (ammoType: Ammo): AmmoMeta => {
        if (!this.ammoTypeToMeta) {
            this.ammoTypeToMeta = new Map()
            this.ammoTypeToMeta.set("webAmmo", {
                spriteId: "arrow-brown.png",
                effects: ["webslow"],
                createNpcProps: {
                    spriteId: "spiderweb-exact.png",
                    flags: {
                        contactEffects: ["webslow"],
                    },
                    msUntilExpiration: 5000,
                    dimensions: {
                        w: TileSize * 2,
                        h: TileSize * 2,
                    },
                },
            })
            this.ammoTypeToMeta.set("arrow", {
                spriteId: "arrow-brown.png",
                // createNpcProps: {
                //     spriteId: "arrow-brown.png",
                //     msUntilExpiration: 5000,
                // }
            })
        }
        return this.ammoTypeToMeta.get(ammoType)
    }

    static resolveProjectileSpriteId = (weaponType: WeaponType) => {
        const characteristic = WeaponCharacteristics[weaponType]
        if (!characteristic) {
            return
        }

        const spriteId = characteristic.bulletSpriteId
        if (spriteId) {
            return spriteId
        }
        const bulletType = characteristic.bulletType
        if (!bulletType) {
            return
        }

        let ammoMeta: AmmoMeta
        return ammoMeta?.spriteId
    }

    static isStunTypeWeapon = (weaponType: WeaponType) => {
        const characteristic = WeaponCharacteristics[weaponType]
        if (!characteristic) {
            return false
        }

        const bulletType = characteristic.bulletType
        if (!bulletType) {
            return false
        }

        return this.resolveAmmoMetaFromAmmo(bulletType)?.effects?.includes("webslow")
    }
}

export type ItemCondition = "new" | "sturdy" | "worn" | "fragile"

class Durability {
    static computeMaxDurability = (itemTypeMeta: ItemTypeMeta): number | undefined => {
        if (!Properties.hasDurability(itemTypeMeta)) {
            return
        }

        const material = Material.computeItemMaterial(itemTypeMeta)
        if (material === "metal") {
            return 100
        }
        if (material === "wood") {
            return 70
        }
        // todo - this should be more sophisticated, based on type and composition of the item
        return 100
    }

    static computeItemConditionPct = (inventoryItem: InventoryItem): number => {
        const maxDurabilty = this.computeMaxDurability(inventoryItem.itemTypeMeta)
        if (maxDurabilty === undefined) {
            return
        }
        const { hp = 0 } = inventoryItem
        const pct = hp / maxDurabilty
        return pct
    }

    static computeItemCondition = (inventoryItem: InventoryItem): ItemCondition | undefined => {
        const pct = this.computeItemConditionPct(inventoryItem)
        if (pct === undefined) {
            return
        }

        if (pct > 0.75) {
            return "new"
        }

        if (pct > 0.5) {
            return "sturdy"
        }

        if (pct > 0.25) {
            return "worn"
        }

        return "fragile"
    }
}

class Material {
    // todo - make this better
    static computeItemMaterial = (itemTypeMeta: ItemTypeMeta): ItemMaterial => {
        if (itemTypeMeta.class === "weapon") {
            if (["brown", "lightbrown"].includes(itemTypeMeta.color)) {
                return "wood"
            }
        }

        return "metal"
    }
}

export interface AddItemToInventoryResults {
    success: boolean
    excessQuantity?: number
    updatedEntity?: Entity
    updatedItem?: InventoryItem
    inventoryItemAdded?: boolean
    addedItems?: InventoryItem[]
}

export interface AddItemToInventoryProps {
    entity: Entity
    sourceItemEntity?: Entity
    sourceItem?: InventoryItem
    item: ItemTypeMeta
    quantity: number
}

export type CreateNewItemProps = AddItemToInventoryProps
export interface CreateNewItemResults {
    updatedEntity: Entity
    createdItem: InventoryItem
}

class InventoryMechanics {
    static stackLimit = () => 50

    static inventoryFull = (entity: Entity): boolean => {
        return entity.inventory?.items?.length > maxInventoryItemsSize
    }

    static canReceive = (receiver: Entity, itemType: ItemTypeMeta, quantity: number): boolean => {
        const isStackable = Properties.stackable(itemType)
        const stackLimit = this.stackLimit()
        const inventorySize = receiver.inventory?.items?.length
        const allSlotsOccupied = inventorySize >= maxInventoryItemsSize
        if (!isStackable && allSlotsOccupied) {
            return false
        }

        const existingItem = locateInventoryItemObjectByItemTypeMeta(receiver.inventory, itemType, stackLimit)
        const maximumNumberOfNewSlots = maxInventoryItemsSize - inventorySize
        let quantityForExistingItem = 0
        if (existingItem) {
            // update the existing item up to its capacity
            quantityForExistingItem = stackLimit - (existingItem.quantity || 1)
        }

        const quantityForNewItems = quantity - quantityForExistingItem
        const numberOfNewItems = Math.ceil(quantityForNewItems / stackLimit)

        if (numberOfNewItems > maximumNumberOfNewSlots) {
            return false
        }

        return true
    }

    // The largest slice of `desired` the receiver can actually hold. canReceive is monotonic in
    // quantity -- room for n implies room for anything smaller -- so this binary searches it rather
    // than restating its slot/stack arithmetic. Used to size the transfer quantity picker: the slider
    // must not offer an amount that would be silently trimmed on arrival.
    static receivableQuantity = (receiver: Entity, itemType: ItemTypeMeta, desired: number): number => {
        const want = Math.floor(desired)
        if (want < 1) {
            return 0
        }
        if (this.canReceive(receiver, itemType, want)) {
            return want
        }
        let low = 0
        let high = want - 1
        while (low < high) {
            const mid = Math.ceil((low + high) / 2)
            if (this.canReceive(receiver, itemType, mid)) {
                low = mid
            } else {
                high = mid - 1
            }
        }
        return low
    }

    static createNewItem = ({
        entity,
        sourceItemEntity,
        sourceItem,
        item,
        quantity,
    }: CreateNewItemProps): CreateNewItemResults => {
        // create new item because there's no existing stackable item to add to,
        // or that item is full up wrto stackable limit
        const isStackable = ItemMechanics.properties.stackable(item)
        const hp = !isStackable ? sourceItem?.hp : sourceItemEntity?.hp
        const newItem = createInventoryItem(item, {
            sourceItem: sourceItemEntity,
            quantity,
            hp,
            id: randomId(),
            flags: sourceItem?.flags,
        })
        let updatedEntity = addInventoryItem(entity, newItem)

        if (item.class === "weapon" && !entity.weapon && Mechanics.entity.inventory.canEquipWeapon(entity)) {
            const weaponType = item.type as WeaponType
            entity.weapon = {
                weaponType,
            }
            updatedEntity = activateInventoryItem(updatedEntity, newItem.id)
        }

        if (item.class === "armor") {
            const activeItem = locateInventoryActiveItemObject(entity.inventory, "armor")
            if (!activeItem) {
                updatedEntity = activateInventoryItem(updatedEntity, newItem.id)
            }
        }

        return {
            updatedEntity,
            createdItem: newItem,
        }
    }

    static addItemToInventory = ({
        entity,
        sourceItemEntity,
        sourceItem,
        item,
        quantity = 1,
    }: AddItemToInventoryProps): AddItemToInventoryResults => {
        const currentInventorySize = entity?.inventory?.items.length
        const isFull = currentInventorySize >= maxInventoryItemsSize
        const isStackable = ItemMechanics.properties.stackable(item)
        const stackLimit = isStackable ? InventoryMechanics.stackLimit() : 1
        // Same precedence createNewItem/createInventoryItem use when they decide which flags the new
        // slot carries, so the slot we stack ONTO is the slot we would have created.
        const incomingFlags = sourceItemEntity?.flags ? sourceItemEntity.flags : sourceItem?.flags
        const existingItem = locateInventoryItemObjectByItemTypeMeta(
            entity.inventory,
            item,
            stackLimit,
            incomingFlags?.originEntityTileId,
        )

        const totalQuantity = (existingItem ? existingItem.quantity || 1 : 0) + quantity
        if (totalQuantity < 1) {
            return {
                success: false,
            }
        }

        if (totalQuantity < stackLimit) {
            // the total amount fits within the slot
            if (existingItem) {
                // an existing item exists, update it
                const updatedEntity = updateInventoryItemQuantity(entity, existingItem, quantity)
                return {
                    success: true,
                    updatedEntity,
                    updatedItem: existingItem,
                }
            } else {
                if (isFull) {
                    return {
                        success: false,
                    }
                } else {
                    // an existing item doesn't exist, create one
                    const { updatedEntity, createdItem } = this.createNewItem({
                        entity,
                        sourceItemEntity,
                        sourceItem,
                        item,
                        quantity,
                    })

                    return {
                        success: true,
                        updatedEntity,
                        addedItems: [createdItem],
                    }
                }
            }
        } else {
            // the total amount exceeds the slot, overflow other items if its possible

            const maximumNumberOfNewSlots = maxInventoryItemsSize - entity.inventory.items.length
            let updatedEntity: Entity
            let quantityForExistingItem = 0
            let createdItems: InventoryItem[] = []
            if (existingItem) {
                // update the existing item up to its capacity
                quantityForExistingItem = stackLimit - (existingItem.quantity || 1)
                updatedEntity = updateInventoryItemQuantity(entity, existingItem, quantityForExistingItem)
            }

            // spread the rest to new items
            const quantityForNewItems = quantity - quantityForExistingItem
            const numberOfNewItems = Math.ceil(quantityForNewItems / stackLimit)

            if (isFull && numberOfNewItems > 0) {
                return {
                    success: false,
                    updatedEntity,
                    updatedItem: existingItem,
                    excessQuantity: quantityForNewItems,
                }
            }

            let totalAdded = 0
            for (let i = 0; i < numberOfNewItems; i++) {
                if (i > maximumNumberOfNewSlots - 1) {
                    break
                }
                const itemsToAdd = i == numberOfNewItems - 1 ? quantityForNewItems - i * stackLimit : stackLimit
                const createResults = this.createNewItem({
                    entity: updatedEntity || entity,
                    sourceItemEntity,
                    sourceItem,
                    item,
                    quantity: itemsToAdd,
                })
                updatedEntity = createResults.updatedEntity
                createdItems.push(createResults.createdItem)
                totalAdded += itemsToAdd
            }

            return {
                success: true,
                excessQuantity: quantityForNewItems - totalAdded,
                updatedItem: existingItem,
                updatedEntity,
                addedItems: createdItems,
            }
        }
    }
}

export class ItemMechanics {
    static weight = Weight
    static protection = Protection
    static crafting = Crafting
    static trading = Trading
    static properties = Properties
    static projectile = Projectile
    static durability = Durability
    static material = Material
    static inventory = InventoryMechanics
}
